import { message } from "antd";
import React, { forwardRef, useEffect, useState } from "react";
import { FaLinkedin } from "react-icons/fa";

const MAPS_QUERY = "Downtown Vancouver, BC";
const MAPS_URL = `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(MAPS_QUERY)}`;
const MAPS_EMBED_URL =
  "https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d2603.5987566892684!2d-123.11959!3d49.28273!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x548673f143a94fb7%3A0xbb9196ea9fe0da69!2sDowntown%20Vancouver%2C%20BC!5e0!3m2!1sen!2sca!4v1719000000000!5m2!1sen!2sca";

const Footer = forwardRef(({ attentionKey = 0 }, ref) => {
  const email = "julian.boyko@outlook.com";
  const [contactAttention, setContactAttention] = useState(false);

  useEffect(() => {
    if (attentionKey === 0) return;
    setContactAttention(true);
    const t = window.setTimeout(() => setContactAttention(false), 2100);
    return () => window.clearTimeout(t);
  }, [attentionKey]);

  const handleCopy = () => {
    navigator.clipboard.writeText(email);
    message.success("Email copied to clipboard!");
  };

  return (
    <footer
      ref={ref}
      className="site-footer relative mt-16 overflow-visible bg-gradient-to-br from-brand-800 via-brand-700 to-brand-900 px-4 py-12 text-white md:mt-24 md:px-6"
    >
      {/* Top wavy separator */}
      <svg
        className="absolute top-0 left-0 right-0 -translate-y-full"
        viewBox="0 0 1200 100"
        preserveAspectRatio="none"
        style={{ height: "100px", width: "100%" }}
      >
        <defs>
          <linearGradient id="waveGradient" x1="0%" y1="0%" x2="0%" y2="100%">
            <stop offset="0%" className="footer-wave-from" />
            <stop
              offset="100%"
              style={{ stopColor: "rgb(14, 116, 144)", stopOpacity: 1 }}
            />
          </linearGradient>
        </defs>

        {/* Main wavy divider */}
        <path
          d="M0,30 Q150,5 300,25 T600,20 T900,30 T1200,25 L1200,100 L0,100 Z"
          fill="url(#waveGradient)"
        ></path>

        {/* Additional wave for depth */}
        <path
          d="M0,40 Q200,15 400,35 T800,25 T1200,40 L1200,100 L0,100 Z"
          fill="rgb(14, 116, 144)"
          opacity="0.5"
        ></path>
      </svg>

      <div className="max-w-6xl mx-auto flex flex-col md:flex-row gap-8 md:gap-12 items-center md:items-stretch relative z-10">
        {/* Left Section - Contact Info */}
        <div className="flex-1 flex flex-col justify-center">
          <h2 className="mb-6 font-display text-3xl font-bold md:text-4xl">
            Let's connect
          </h2>

          {/* LinkedIn Link */}
          <div className="mb-6">
            <a
              href="https://www.linkedin.com/in/julian-boyko-3064041a2/"
              target="_blank"
              rel="noopener noreferrer"
              className={`inline-flex transform rounded-full bg-white p-4 shadow-lg transition-all hover:scale-110 hover:bg-brand-50 hover:shadow-xl ${
                contactAttention ? "footer-contact-attention" : ""
              }`}
            >
              <FaLinkedin className="text-2xl text-brand-700" />
            </a>
          </div>

          {/* Email Button */}
          <div className="flex items-center w-full">
            <button
              type="button"
              onClick={handleCopy}
              className={`flex w-full cursor-pointer items-stretch rounded-xl border-2 border-white bg-white p-0 shadow-lg transition-all hover:scale-105 hover:shadow-2xl focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-300 sm:w-auto ${
                contactAttention ? "footer-contact-attention" : ""
              }`}
            >
              <span className="flex w-12 min-h-[2.75rem] shrink-0 items-center justify-center self-stretch rounded-l-lg bg-brand-700 text-2xl font-bold text-white">
                @
              </span>
              <span className="flex min-h-[2.75rem] min-w-0 flex-1 items-center justify-center break-all px-4 py-0 text-sm font-medium leading-tight text-brand-700 max-md:text-center sm:justify-start sm:px-5 sm:py-2 sm:text-left sm:text-base sm:leading-normal">
                {email}
              </span>
            </button>
          </div>
        </div>

        {/* Right Section - Map */}
        <div className="flex-1 relative">
          <div className="absolute -top-8 -right-8 h-32 w-32 rounded-full bg-brand-400 opacity-20 blur-2xl"></div>
          <div className="absolute -bottom-8 -left-8 h-40 w-40 rounded-full bg-brand-300 opacity-15 blur-3xl"></div>

          <div className="relative z-10 flex flex-1 flex-col gap-3">
            <div className="overflow-hidden rounded-3xl border-8 border-white shadow-2xl transition-shadow hover:shadow-3xl h-56 md:h-72 w-full">
              <iframe
                title="Downtown Vancouver Location"
                width="100%"
                height="100%"
                style={{ border: 0 }}
                loading="lazy"
                allowFullScreen
                referrerPolicy="no-referrer-when-downgrade"
                src={MAPS_EMBED_URL}
              />
            </div>
            <a
              href={MAPS_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex w-full items-center justify-center gap-2 rounded-xl border-2 border-white/90 bg-white px-4 py-2.5 text-sm font-semibold text-brand-700 shadow-md transition-all hover:scale-[1.02] hover:bg-brand-50 hover:shadow-lg sm:w-auto sm:self-start"
            >
              Open in Google Maps
            </a>
          </div>
        </div>
      </div>

      {/* Bottom wavy background */}
      <svg
        className="absolute bottom-0 left-0 right-0"
        viewBox="0 0 1200 120"
        preserveAspectRatio="none"
        style={{ height: "80px", width: "100%" }}
      >
        <path
          d="M0,70 Q300,120 600,70 T1200,70 L1200,0 L0,0 Z"
          fill="white"
          opacity="0.1"
        ></path>
        <path
          d="M0,60 Q300,110 600,60 T1200,60 L1200,0 L0,0 Z"
          fill="white"
          opacity="0.15"
        ></path>
      </svg>

      <div className="relative z-10 mx-auto mt-8 max-w-6xl border-t border-white/20 pt-6 text-center text-sm text-white/80">
        <p>© {new Date().getFullYear()} Julian Boyko</p>
      </div>
    </footer>
  );
});

export default Footer;
